// Registers a Quill block-embed blot for rich-media embeds (YouTube, X, etc).
//
// Importing this module registers the blot exactly once. The blot serializes
// to `<div class="amada-embed" data-embed-provider data-embed-url>…</div>`,
// carrying only a provider slug + URL — never arbitrary embed markup.

import ReactQuill from 'react-quill';
import {
  EMBED_CLASS,
  embedCardInnerHtml,
  providerBySlug,
  type EmbedProvider,
} from './embeds';

export const EMBED_BLOT_NAME = 'amadaEmbed';

export interface EmbedBlotValue {
  provider: EmbedProvider;
  url: string;
}

let registered = false;

export function registerEmbedBlot(): void {
  if (registered) return;
  registered = true;

  const Quill = ReactQuill.Quill;
  // Quill's dynamic import isn't precisely typed; the returned base class is a
  // Parchment BlockEmbed constructor.
  const BlockEmbed = Quill.import('blots/block/embed') as {
    new (...args: unknown[]): unknown;
    create(value: unknown): HTMLElement;
  };

  class AmadaEmbedBlot extends (BlockEmbed as { new (...args: unknown[]): object }) {
    static blotName = EMBED_BLOT_NAME;
    static tagName = 'div';
    static className = EMBED_CLASS;

    static create(value: EmbedBlotValue): HTMLElement {
      const node = (BlockEmbed.create as (v: unknown) => HTMLElement).call(this, value);
      const def = providerBySlug(value?.provider ?? '');
      node.setAttribute('data-embed-provider', value?.provider ?? '');
      node.setAttribute('data-embed-url', value?.url ?? '');
      node.setAttribute('contenteditable', 'false');
      if (def && value?.url) {
        node.innerHTML = embedCardInnerHtml(def, value.url);
      }
      return node;
    }

    static value(node: HTMLElement): EmbedBlotValue {
      return {
        provider: (node.getAttribute('data-embed-provider') ?? '') as EmbedProvider,
        url: node.getAttribute('data-embed-url') ?? '',
      };
    }
  }

  Quill.register({ [`formats/${EMBED_BLOT_NAME}`]: AmadaEmbedBlot }, true);
}
